/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import {
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeTypes, useThemeContext } from '@opengeoweb/theme';
import { SHARED_NAMESPACE } from '../../utils/i18n';

interface MenuItemThemeProps {
  enableSpecialThemes?: boolean;
}

const MenuItemTheme: React.FC<MenuItemThemeProps> = ({
  enableSpecialThemes,
}) => {
  const { namedTheme, setThemeName } = useThemeContext();
  const { t } = useTranslation(SHARED_NAMESPACE);
  return (
    <MenuItem divider>
      <Grid container>
        <Grid>{t('shared-choose-app-mode')}</Grid>
        <Grid>
          <RadioGroup
            onChange={(event, value) => {
              if (value && setThemeName) {
                setThemeName(value as ThemeTypes);
              }
            }}
          >
            <FormControlLabel
              control={
                <Radio checked={namedTheme?.name === ThemeTypes.LIGHT_THEME} />
              }
              label={t('shared-light')}
              value={ThemeTypes.LIGHT_THEME}
            />
            <FormControlLabel
              control={
                <Radio checked={namedTheme?.name === ThemeTypes.DARK_THEME} />
              }
              label={t('shared-dark')}
              value={ThemeTypes.DARK_THEME}
            />

            {enableSpecialThemes ? (
              <>
                <FormControlLabel
                  control={
                    <Radio
                      checked={namedTheme?.name === ThemeTypes.DEMO_THEME}
                    />
                  }
                  label="Demo"
                  value={ThemeTypes.DEMO_THEME}
                />
                <FormControlLabel
                  control={
                    <Radio
                      checked={namedTheme?.name === ThemeTypes.XMAS_THEME}
                    />
                  }
                  label={`${t('shared-xmas')}`}
                  value={ThemeTypes.XMAS_THEME}
                />
                <FormControlLabel
                  control={
                    <Radio
                      checked={namedTheme?.name === ThemeTypes.EUMETNET_THEME}
                    />
                  }
                  label="EUMETNET"
                  value={ThemeTypes.EUMETNET_THEME}
                />
              </>
            ) : null}
          </RadioGroup>
        </Grid>
      </Grid>
    </MenuItem>
  );
};

export default MenuItemTheme;
